const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export interface Scenario {
  id: string;
  name: string;
  description?: string;
  prompt_template: string;
  variables: string[];
  category?: string;
  is_default: boolean;
  is_active: boolean;
  usage_count: number;
  created_at: string;
  updated_at?: string;
  sms_intro_template?: string | null;
  sms_context_prompt?: string | null;
}

export interface ScenarioRequest {
  name: string;
  description?: string;
  prompt_template: string;
  variables: string[];
  category?: string;
  is_active: boolean;
  sms_intro_template?: string | null;
  sms_context_prompt?: string | null;
}

class ScenarioService {
  async getScenarios(): Promise<Scenario[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/scenarios/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch scenarios');
      }

      const scenarios = await response.json();

      // If API returns empty array, fall back to default scenarios
      if (Array.isArray(scenarios) && scenarios.length === 0) {
        console.log('API returned empty scenarios, using fallback defaults');
        return this.getDefaultScenarios();
      }

      return scenarios;
    } catch (error) {
      console.error('Error fetching scenarios:', error);
      // Return default scenarios if API fails
      return this.getDefaultScenarios();
    }
  }

  async getScenario(id: string): Promise<Scenario | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/scenarios/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error('Failed to fetch scenario');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching scenario:', error);
      return null;
    }
  }

  async createScenario(scenario: ScenarioRequest): Promise<Scenario | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/scenarios/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`,
        },
        body: JSON.stringify(scenario),
      });

      if (!response.ok) {
        throw new Error('Failed to create scenario');
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating scenario:', error);
      return null;
    }
  }

  async updateScenario(id: string, scenario: ScenarioRequest): Promise<Scenario | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/scenarios/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`,
        },
        body: JSON.stringify(scenario),
      });

      if (!response.ok) {
        throw new Error('Failed to update scenario');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating scenario:', error);
      return null;
    }
  }

  async deleteScenario(id: string): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/scenarios/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`,
        },
      });

      return response.ok;
    } catch (error) {
      console.error('Error deleting scenario:', error);
      return false;
    }
  }

  async trackUsage(id: string): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/scenarios/${id}/use`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`,
        },
      });

      return response.ok;
    } catch (error) {
      console.error('Error tracking scenario usage:', error);
      return false;
    }
  }

  // Fallback default scenarios with OpenAI assistant optimized prompts
  private getDefaultScenarios(): Scenario[] {
    return [
      {
        id: 'car-trade-in',
        name: 'Car Trade-In Offer',
        description: 'Encourage customers to trade in their current vehicle for a newer model',
        prompt_template: `Hi {name}! 👋

I'm reaching out from Doxa Auto Group with exciting news about your {vehicle}.

**Great News:** The trade-in market for your vehicle is particularly strong right now, and we have some fantastic newer models that might interest you.

**Here's what we can offer:**
- Competitive trade-in value for your current {vehicle}
- Special financing rates starting at 1.9% APR
- Many customers are upgrading with similar or even lower monthly payments

**Customer Context for AI:**
- Customer Name: {name}
- Current Vehicle: {vehicle}
- Last Service: {last_service}
- Phone: {phone}

Would you be interested in learning what your {vehicle} is worth in today's market? I can provide you with a no-obligation trade-in estimate right now!

**Instructions for AI Assistant:**
- You are a friendly automotive sales representative from Doxa Auto Group
- The customer owns a {vehicle}
- Focus on the benefits of upgrading: reliability, warranty, latest features
- Ask about their current vehicle's condition, mileage, and any issues
- Offer to schedule an appraisal appointment
- Be helpful but not pushy - build trust first
- If they show interest, offer to schedule a test drive of newer models`,
        sms_intro_template: "Hi {name}, it's Doxa Auto Group. Your {vehicle} has great trade-in value right now—reply if you'd like an upgrade estimate.",
        sms_context_prompt: "You are texting {name} about their {vehicle} to highlight strong trade-in value and upgrade options. Reply with one UK-English SMS under 160 characters inviting them to respond.",
        variables: ['name', 'vehicle', 'last_service', 'phone'],
        category: 'trade-in',
        is_default: true,
        is_active: true,
        usage_count: 0,
        created_at: new Date().toISOString(),
      },
      {
        id: 'car-service-reminder',
        name: 'Car Service Reminder',
        description: 'Remind customers about scheduled maintenance and service needs',
        prompt_template: `Hello {name}! 👋

I hope you're enjoying your {vehicle}! I'm reaching out from Doxa Auto Group regarding your vehicle's maintenance schedule.

**Service Reminder:**
- Last Service: {last_service}
- Based on your vehicle's age and typical usage, it may be time for your next service appointment
- Regular maintenance helps keep your {vehicle} running smoothly and prevents costly repairs

**Customer Context for AI:**
- Customer Name: {name}
- Vehicle: {vehicle}
- Last Service Date: {last_service}
- Phone: {phone}

Is there anything specific you've noticed with your {vehicle} lately? Any unusual sounds, performance issues, or dashboard warning lights?

**Instructions for AI Assistant:**
- You are a knowledgeable automotive service advisor from Doxa Auto Group
- The customer owns a {vehicle}
- Ask about current vehicle performance and any concerns
- Recommend appropriate service based on vehicle age, mileage, and last service date
- Explain the importance of preventive maintenance
- Offer to schedule a convenient service appointment
- Mention any current service specials or discounts
- Be helpful and educational - build trust through expertise
- If they mention any issues, show concern and urgency for safety`,
        sms_intro_template: "Hi {name}, your {vehicle} is due a service. Reply to book a slot with Mooney's Hyundai and keep things running smoothly.",
        sms_context_prompt: "You are sending a service reminder SMS to {name} about their {vehicle}. Reply with one UK-English message under 160 characters inviting them to schedule at Mooney's Hyundai.",
        variables: ['name', 'vehicle', 'last_service', 'phone'],
        category: 'service',
        is_default: true,
        is_active: true,
        usage_count: 0,
        created_at: new Date().toISOString(),
      },
      {
        id: 'promo-deals',
        name: 'Promotional Deals',
        description: 'Share exclusive deals, promotions, and special offers',
        prompt_template: `Hi {name}! 🎉

Exciting news from Doxa Auto Group! As one of our valued customers with your {vehicle}, you've been selected for our exclusive customer appreciation promotion.

**Limited Time Offers (This Month Only):**
- Special financing rates starting at 1.9% APR
- Bonus trade-in values up to €3,000 above market value
- Extended warranty options at special pricing
- Free service package with any vehicle purchase

**Customer Context for AI:**
- Customer Name: {name}
- Current Vehicle: {vehicle}
- Customer Type: Valued existing customer
- Phone: {phone}

**Your Exclusive Benefits:**
✓ Priority access to our latest models
✓ VIP customer pricing
✓ Dedicated sales consultant
✓ Express financing approval

**Instructions for AI Assistant:**
- You are an enthusiastic but professional sales representative from Doxa Auto Group
- The customer is a valued existing customer with a {vehicle}
- Emphasize the exclusivity and limited-time nature of the offers
- Ask about their current satisfaction with their vehicle
- Find out if they've been considering an upgrade or new features
- Highlight specific benefits that match their needs
- Create urgency but remain helpful and genuine
- Offer to schedule a no-pressure consultation to explore options
- If interested, book an appointment for them to see available inventory`,
        sms_intro_template: "Hi {name}, Mooney's Hyundai has exclusive offers perfect for your {vehicle}. Reply to explore tailored upgrades or incentives.",
        sms_context_prompt: "You are sending a promotional SMS to {name} about exclusive offers relevant to their {vehicle}. Respond with one UK-English message under 160 characters highlighting the offer and inviting them to reply.",
        variables: ['name', 'vehicle', 'phone'],
        category: 'promotion',
        is_default: true,
        is_active: true,
        usage_count: 0,
        created_at: new Date().toISOString(),
      },
    ];
  }

  // Migration helper: load scenarios from localStorage and migrate to API
  async migrateFromLocalStorage(): Promise<void> {
    try {
      const savedPrompts = localStorage.getItem('simulatorPrompts');
      if (!savedPrompts) {
        return;
      }

      const prompts = JSON.parse(savedPrompts);
      const scenarios = this.getDefaultScenarios();

      // Update scenarios with localStorage prompts
      for (const scenario of scenarios) {
        if (prompts[scenario.id] && prompts[scenario.id] !== scenario.prompt_template) {
          // Update the scenario in the database
          await this.updateScenario(scenario.id, {
            name: scenario.name,
            description: scenario.description,
            prompt_template: prompts[scenario.id],
            variables: scenario.variables,
            category: scenario.category,
            is_active: scenario.is_active,
            sms_intro_template: scenario.sms_intro_template ?? null,
            sms_context_prompt: scenario.sms_context_prompt ?? null,
          });
        }
      }

      // Clear localStorage after successful migration
      localStorage.removeItem('simulatorPrompts');
      console.log('Successfully migrated scenarios from localStorage to database');
    } catch (error) {
      console.error('Error migrating scenarios from localStorage:', error);
    }
  }
}

export const scenarioService = new ScenarioService();
